/*
 * Figma `121:232` — OPS-02 · Tasks · Board + Dependencies.
 *
 * `TableTemplate` with a board above it: three columns — ต้องทำ, กำลังทำ, ติดขัด — then the full
 * list, then the task you opened in a drawer.
 *
 * The frame's brief for the operational screens is "optimise time-to-decision rather than display
 * data", and for a task board that means one thing: **ติดขัด is not a status somebody sets**. It is
 * the set of a task's dependencies that are not finished yet. Treating it as a status is how a
 * board keeps showing a task as blocked for two days after the thing blocking it was done, which is
 * exactly the failure this column exists to prevent. `blockersOf` derives it, and the card names
 * who it is waiting on rather than saying "blocked".
 */
import { useState } from 'react';
import {
  Alert, Button, Card, DateTimeField, Drawer, FormDialog, KpiCard, Select, StatusBadge,
  TableTemplate, Toggle,
  TextField, Textarea,
} from '../..';
import type { BadgeTone, Column } from '../..';
import type { Destination } from '../../destinations';
import type { OpsTaskDto, OpsTaskPriority, OpsTaskStatus } from '../../../api';
import type { OpsTasksState } from './useOpsTasks';
import { useOpsTasks } from './useOpsTasks';

const STATUS_LABEL: Record<OpsTaskStatus, string> = {
  todo: 'ต้องทำ', in_progress: 'กำลังทำ', pending_approval: 'รออนุมัติ', done: 'เสร็จแล้ว',
};
const PRIORITY_LABEL: Record<OpsTaskPriority, string> = {
  low: 'ต่ำ', medium: 'ปานกลาง', high: 'สูง', urgent: 'ด่วน',
};
const PRIORITY_TONE: Record<OpsTaskPriority, BadgeTone> = {
  low: 'neutral', medium: 'neutral', high: 'warning', urgent: 'danger',
};

const dayLabel = (iso: string) => (iso ? new Date(iso).toLocaleDateString('th-TH') : '—');

/** Past its SLA and not finished. Overdue is a fact about time, not a status anyone sets either. */
export function isOverdue(task: OpsTaskDto, now = Date.now()): boolean {
  if (task.status === 'done' || !task.slaDate) return false;
  return new Date(task.slaDate).getTime() < now;
}

export function OpsTasksScreen({ destination, state, permitted, role }: {
  destination: Destination;
  state: OpsTasksState;
  permitted: boolean;
  role?: string | null;
}) {
  const {
    tasks, departments, staff, blockers, loading, loadError, error, busyId, setDependencies,
    create, setStatus, update, remove, reload,
  } = state;
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftSla, setDraftSla] = useState('');

  const open = openId ? tasks.find((t) => t.id === openId) ?? null : null;
  const blockedTasks = tasks.filter((t) => t.status !== 'done' && (blockers.get(t.id)?.length ?? 0) > 0);
  const [linking, setLinking] = useState<OpsTaskDto | null>(null);
  const [linkDraft, setLinkDraft] = useState<string[]>([]);
  const overdue = tasks.filter((t) => isOverdue(t));
  const unowned = tasks.filter((t) => t.status !== 'done' && !t.ownerStaffId && !t.importedOwnerName.trim());

  const columnFor = (status: OpsTaskStatus) => tasks.filter((t) => t.status === status);

  const columns: Column<OpsTaskDto>[] = [
    {
      key: 'title',
      header: 'งาน',
      cell: (t) => {
        const blocking = blockers.get(t.id) ?? [];
        return (
          <span className="jt-cell-stack">
            <span>
              {t.isMilestone && <StatusBadge tone="accent">MILESTONE</StatusBadge>}
              {t.isMilestone ? ' ' : ''}{t.title}
            </span>
            {blocking.length > 0 && (
              <span className="jt-cell-sub">รอ {blocking.map((b) => b.title).join(' · ')}</span>
            )}
          </span>
        );
      },
    },
    {
      key: 'owner',
      header: 'ผู้รับผิดชอบ',
      width: '180px',
      cell: (t) => (t.ownerName || t.importedOwnerName || <StatusBadge tone="warning">ยังไม่มีเจ้าของ</StatusBadge>),
    },
    { key: 'dept', header: 'ฝ่าย', width: '150px', cell: (t) => t.departmentName ?? '—' },
    {
      key: 'sla',
      header: 'กำหนดเสร็จ',
      width: '160px',
      cell: (t) => (
        <span className="jt-cell-stack">
          <span>{dayLabel(t.slaDate)}</span>
          {isOverdue(t) && <StatusBadge tone="danger">เลยกำหนด</StatusBadge>}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'สถานะ',
      width: '180px',
      cell: (t) => (
        <span className="jt-inline-actions">
          <StatusBadge tone={t.status === 'done' ? 'success' : 'neutral'}>{STATUS_LABEL[t.status]}</StatusBadge>
          <StatusBadge tone={PRIORITY_TONE[t.priority]}>{PRIORITY_LABEL[t.priority]}</StatusBadge>
        </span>
      ),
    },
  ];

  return (
    <TableTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload, permitted, role }}
      kpis={[
        <KpiCard key="all" label="งานทั้งหมด" value={tasks.length.toLocaleString('th-TH')} />,
        <KpiCard
          key="blocked"
          label="ติดขัด"
          value={blockedTasks.length.toLocaleString('th-TH')}
          tone={blockedTasks.length > 0 ? 'warning' : undefined}
          meta="รองานอื่นที่ยังไม่เสร็จ"
        />,
        <KpiCard
          key="overdue"
          label="เลยกำหนด"
          value={overdue.length.toLocaleString('th-TH')}
          tone={overdue.length > 0 ? 'danger' : undefined}
        />,
        <KpiCard
          key="unowned"
          label="ยังไม่มีเจ้าของ"
          value={unowned.length.toLocaleString('th-TH')}
          tone={unowned.length > 0 ? 'warning' : undefined}
        />,
      ]}
      summary={
        <>
          {error && <Alert tone="danger" title="ทำรายการไม่สำเร็จ">{error}</Alert>}
          {blockedTasks.length > 0 && (
            <Alert tone="warning" title={`${blockedTasks.length} งานรองานอื่นอยู่`}>
              {blockedTasks.slice(0, 3).map((t) => `${t.title} รอ ${(blockers.get(t.id) ?? []).map((b) => b.title).join(', ')}`).join(' · ')}
              {blockedTasks.length > 3 && ' และอื่น ๆ'}
            </Alert>
          )}
          <div className="jt-board">
            {(['todo', 'in_progress', 'pending_approval'] as OpsTaskStatus[]).map((status) => (
              <Card key={status} title={`${STATUS_LABEL[status]} · ${columnFor(status).length}`}>
                {columnFor(status).length === 0 && <p className="jt-field-hint">ไม่มีงานในคอลัมน์นี้</p>}
                {columnFor(status).slice(0, 6).map((task) => {
                  const blocking = blockers.get(task.id) ?? [];
                  return (
                    <button
                      key={task.id}
                      type="button"
                      className="jt-account"
                      data-selected={openId === task.id || undefined}
                      onClick={() => setOpenId(task.id)}
                    >
                      <span className="jt-account-title">{task.title}</span>
                      <span className="jt-account-sub">
                        {task.ownerName || task.importedOwnerName || 'ยังไม่มีเจ้าของ'} · {dayLabel(task.slaDate)}
                      </span>
                      {blocking.length > 0 && (
                        <span className="jt-account-tags">
                          <StatusBadge tone="warning">{`รอ ${blocking.length} งาน`}</StatusBadge>
                        </span>
                      )}
                      {isOverdue(task) && (
                        <span className="jt-account-tags"><StatusBadge tone="danger">เลยกำหนด</StatusBadge></span>
                      )}
                    </button>
                  );
                })}
                {columnFor(status).length > 6 && (
                  <p className="jt-field-hint">และอีก {columnFor(status).length - 6} งาน — ดูทั้งหมดในตารางด้านล่าง</p>
                )}
              </Card>
            ))}
          </div>
        </>
      }
      toolbar={
        adding ? (
          <>
            <TextField label="ชื่องาน" value={draftTitle} onChange={(e) => setDraftTitle(e.target.value)} />
            <DateTimeField label="กำหนดเสร็จ" mode="date" value={draftSla} onChange={(e) => setDraftSla(e.target.value)} />
            <Button
              size="sm"
              variant="primary"
              disabled={!draftTitle.trim() || !draftSla}
              onClick={async () => {
                await create({ title: draftTitle.trim(), slaDate: draftSla } as never);
                setDraftTitle('');
                setDraftSla('');
                setAdding(false);
              }}
            >
              สร้างงาน
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>ยกเลิก</Button>
          </>
        ) : (
          <Button size="sm" variant="primary" onClick={() => setAdding(true)}>＋ เพิ่มงาน</Button>
        )
      }
      table={{
        label: 'แผนงานทั้งหมด',
        columns,
        rows: tasks,
        rowKey: (t) => t.id,
        emptyTitle: 'ยังไม่มีงานในแผน',
        emptyText: 'เพิ่มงานแรกเพื่อเริ่มติดตามความคืบหน้า',
        onRowClick: (t) => setOpenId(t.id),
        activeRowKey: openId,
        card: (t) => (
          <span className="jt-cell-stack">
            <span>{t.title}</span>
            <span className="jt-cell-sub">{t.ownerName || t.importedOwnerName || 'ยังไม่มีเจ้าของ'} · {dayLabel(t.slaDate)}</span>
            <StatusBadge tone={t.status === 'done' ? 'success' : 'neutral'}>{STATUS_LABEL[t.status]}</StatusBadge>
          </span>
        ),
      }}
      drawer={
        <>
        <Drawer
          open={!!open}
          onClose={() => setOpenId(null)}
          title={open?.title ?? ''}
          footer={open && (
            <>
              <Button variant="ghost" disabled={busyId === open.id} onClick={() => void remove(open)}>ลบงาน</Button>
              {open.status !== 'done' && (
                <Button variant="primary" disabled={busyId === open.id} onClick={() => void setStatus(open, 'done')}>
                  ทำเสร็จแล้ว
                </Button>
              )}
            </>
          )}
        >
          {open && (
            <>
              {(blockers.get(open.id) ?? []).length > 0 && (
                <Alert tone="warning" title="งานนี้รองานอื่นอยู่">
                  {(blockers.get(open.id) ?? []).map((b) => b.title).join(' · ')}
                  {' '}— ทำเสร็จได้ แต่ลำดับจริงจะยังติดจนกว่างานเหล่านั้นจะเสร็จ
                </Alert>
              )}
              {isOverdue(open) && (
                <Alert tone="danger" title="เลยกำหนดแล้ว">
                  กำหนดเสร็จคือ {dayLabel(open.slaDate)} — เลื่อนกำหนด หรือแจ้งผู้รับผิดชอบ
                </Alert>
              )}

              {/*
                * "ขึ้นกับ" is what the board's ติดขัด column is derived from, and until now it
                * could only be read. The server checks the whole graph and refuses an edge that
                * would close a cycle; the hook turns the path it names into titles, so a refusal
                * says which link to cut rather than only that one is needed.
                */}
              <Card
                title="ขึ้นกับงานอื่น"
                sub={open.dependsOn.length === 0
                  ? 'ยังไม่ได้ผูกกับงานไหน — งานนี้เริ่มได้ทันที'
                  : `ต้องรอ ${open.dependsOn.length} งานให้เสร็จก่อน`}
              >
                {open.dependsOn.length > 0 && (
                  <ul className="jt-order-lines">
                    {open.dependsOn.map((id) => {
                      const dep = tasks.find((t) => t.id === id);
                      return (
                        <li key={id}>
                          <span>{dep?.title ?? id}</span>
                          <span>{dep ? STATUS_LABEL[dep.status] : '—'}</span>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {permitted && (
                  <div className="jt-inline-actions">
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busyId === open.id || tasks.length < 2}
                      onClick={() => { setLinkDraft([...open.dependsOn]); setLinking(open); }}
                    >
                      แก้ลำดับก่อนหลัง
                    </Button>
                    {tasks.length < 2 && (
                      <span className="jt-field-hint">ต้องมีงานอย่างน้อยสองงานจึงจะผูกลำดับได้</span>
                    )}
                  </div>
                )}
              </Card>

              <Card title="รายละเอียด">
                <Select
                  label="สถานะ"
                  value={open.status}
                  disabled={busyId === open.id}
                  options={(Object.keys(STATUS_LABEL) as OpsTaskStatus[]).map((s) => ({ value: s, label: STATUS_LABEL[s] }))}
                  onChange={(e) => void setStatus(open, e.target.value as OpsTaskStatus)}
                />
                <Select
                  label="ความสำคัญ"
                  value={open.priority}
                  disabled={busyId === open.id}
                  options={(Object.keys(PRIORITY_LABEL) as OpsTaskPriority[]).map((p) => ({ value: p, label: PRIORITY_LABEL[p] }))}
                  onChange={(e) => void update(open, { priority: e.target.value as OpsTaskPriority })}
                />
                <Select
                  label="ผู้รับผิดชอบ"
                  value={open.ownerStaffId ?? ''}
                  disabled={busyId === open.id}
                  options={[{ value: '', label: 'ยังไม่มีเจ้าของ' }, ...staff.map((s) => ({ value: s.id, label: s.displayName }))]}
                  onChange={(e) => void update(open, { ownerStaffId: e.target.value || null })}
                  hint="งานที่ไม่มีเจ้าของคืองานที่ไม่มีใครรู้ว่าต้องทำ"
                />
                <Select
                  label="ฝ่าย"
                  value={open.departmentId ?? ''}
                  disabled={busyId === open.id}
                  options={[{ value: '', label: 'ไม่ระบุฝ่าย' }, ...departments.map((d) => ({ value: d.id, label: d.name }))]}
                  onChange={(e) => void update(open, { departmentId: e.target.value || null })}
                />
                <Textarea
                  label="คำอธิบาย"
                  value={open.description}
                  onChange={(e) => void update(open, { description: e.target.value })}
                />
                {/*
                  * TICK-143 — a milestone is this task, marked. It is not a second kind of record,
                  * so it is a switch on the task rather than a separate thing to create.
                  */}
                <Toggle
                  label="เป็น milestone ของงานนี้"
                  checked={open.isMilestone}
                  disabled={busyId === open.id}
                  onChange={(e) => void update(open, { isMilestone: e.target.checked })}
                  consequence="วันนี้จะขึ้นบนปฏิทินและถูกนับใน Command Center — งานยังทำงานเหมือนเดิมทุกอย่าง"
                />
              </Card>

              <p className="jt-field-hint">
                แก้ไขล่าสุดโดย {open.updatedBy} · เวอร์ชัน {open.version} — ถ้ามีคนอื่นแก้พร้อมกัน ระบบจะปฏิเสธและให้โหลดใหม่
              </p>
            </>
          )}
        </Drawer>

        <FormDialog
          open={linking !== null}
          onClose={() => setLinking(null)}
          onSubmit={() => {
            const target = linking;
            if (!target) return;
            // Stays open when the server refuses: the cycle it names is about these very boxes.
            void setDependencies(target, linkDraft).then((ok) => { if (ok) setLinking(null); });
          }}
          title={`ขึ้นกับงานอื่น · ${linking?.title ?? ''}`}
          description="เลือกงานที่ต้องเสร็จก่อนงานนี้ — ถ้าการเลือกทำให้เกิดวงจร ระบบจะบอกว่าเส้นทางไหนวนกลับมา"
          submitLabel="บันทึกลำดับ"
          busy={busyId === linking?.id}
          error={error || null}
        >
          {tasks.filter((t) => t.id !== linking?.id).map((t) => (
            <Toggle
              key={t.id}
              label={t.title}
              checked={linkDraft.includes(t.id)}
              onChange={(e) => setLinkDraft(e.target.checked
                ? [...linkDraft, t.id]
                : linkDraft.filter((id) => id !== t.id))}
              consequence={t.status === 'done' ? 'เสร็จแล้ว — ผูกได้ แต่จะไม่ทำให้งานนี้ติดขัด' : undefined}
            />
          ))}
        </FormDialog>
        </>
      }
    />
  );
}

export function OpsTasksDestination({ token, eventSlug, destination, permitted, role }: {
  token: string;
  eventSlug?: string;
  destination: Destination;
  permitted: boolean;
  role?: string | null;
}) {
  const state = useOpsTasks(token, eventSlug);
  return <OpsTasksScreen destination={destination} state={state} permitted={permitted} role={role} />;
}
